import { useState } from "react"
import { Card, CardContent, CardHeader } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { StyleChallenge } from "@/components/apex/StyleChallenge"
import { AnimatedCard } from "@/components/apex/AnimatedCard"
import { SocialFeed } from "@/components/apex/SocialFeed"
import { useApexState } from "@/hooks/useApexState"
import { Trophy, Camera, Calendar, Users, ChevronRight, Plus } from "lucide-react"

const activeChallenge = {
  id: "challenge-1",
  title: "Motion & Form",
  subtitle: "Style your Kinetic Joggers in an architectural setting",
  deadline: "3 days left",
  reward: 500,
  participants: 127,
  image: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=600&h=400&fit=crop"
}

const communityPosts = [
  {
    id: "post-1",
    user: {
      name: "Maya Rodriguez",
      tier: "architect",
      avatar: "https://images.unsplash.com/photo-1494790108755-2616b67e1c04?w=40&h=40&fit=crop"
    },
    content: {
      text: "Urban exploration in the new Architect Hoodie ✨ The fit is incredible for movement",
      image: "https://images.unsplash.com/photo-1556821840-3a63f95609a7?w=400&h=500&fit=crop",
      hashtags: ["#WearingGameshape", "#ArchitectHoodie", "#UrbanExploration"]
    },
    engagement: {
      likes: 89,
      comments: 12,
      shares: 7
    },
    timeAgo: "2 hours ago",
    isLiked: false,
    isFeatured: true
  },
  {
    id: "post-2",
    user: {
      name: "Jordan Kim",
      tier: "creator",
      avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=40&h=40&fit=crop"
    },
    content: {
      text: "Morning run in the Kinetic Joggers. Perfect for the Motion & Form challenge!",
      image: "https://images.unsplash.com/photo-1556905055-8f358a7a47b2?w=400&h=500&fit=crop",
      hashtags: ["#ShapeMyWorld", "#MotionAndForm", "#MorningRun"]
    },
    engagement: {
      likes: 34,
      comments: 8,
      shares: 3
    },
    timeAgo: "5 hours ago",
    isLiked: true
  },
  {
    id: "post-3",
    user: {
      name: "Alex Chen",
      tier: "creator",
      avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=40&h=40&fit=crop"
    },
    content: {
      text: "Studio training session. The Form Tank moves with you perfectly 🔥",
      image: "https://images.unsplash.com/photo-1583900985737-6d0495555783?w=400&h=500&fit=crop",
      hashtags: ["#StudioTraining", "#FormTank", "#WearingGameshape"]
    },
    engagement: {
      likes: 56,
      comments: 15,
      shares: 4
    },
    timeAgo: "1 day ago",
    isLiked: false
  }
]

const upcomingEvents = [
  {
    id: "event-1",
    title: "Virtual Q&A with Creative Director",
    date: "Tomorrow, 7PM PST",
    type: "Virtual Event",
    attendees: 89
  },
  {
    id: "event-2", 
    title: "NYC Run Club Meetup",
    date: "This Saturday, 9AM EST",
    type: "In-Person",
    attendees: 24
  }
]

export function ApexStudio() {
  const { state } = useApexState()
  const [showChallenge, setShowChallenge] = useState(false)
  const [activeView, setActiveView] = useState<'feed' | 'challenges' | 'events'>('feed')

  // If showing challenge details, render the challenge component
  if (showChallenge) {
    return (
      <div className="min-h-screen bg-background pb-20">
        <header className="sticky top-0 z-40 border-b border-border bg-card/80 backdrop-blur-lg">
          <div className="max-w-md mx-auto px-4 py-3">
            <div className="flex items-center justify-between">
              <h1 className="text-xl font-bold text-foreground">Style Challenge</h1>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setShowChallenge(false)}
              >
                ← Back
              </Button>
            </div>
          </div>
        </header>

        <div className="max-w-md mx-auto px-4 py-6">
          <StyleChallenge onClose={() => setShowChallenge(false)} />
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background pb-20">
      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-border bg-card/80 backdrop-blur-lg">
        <div className="max-w-md mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <h1 className="text-xl font-bold text-foreground">Studio</h1>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" className="p-2">
                <Plus size={20} />
              </Button>
              <Button variant="ghost" size="sm" className="p-2">
                <Camera size={20} />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <div className="max-w-md mx-auto px-4 pt-4">
        <div className="flex items-center gap-1 mb-6">
          {[
            { id: 'feed', label: 'Feed', count: null },
            { id: 'challenges', label: 'Challenges', count: 1 },
            { id: 'events', label: 'Events', count: 3 }
          ].map(tab => (
            <Button
              key={tab.id}
              variant={activeView === tab.id ? "default" : "ghost"}
              size="sm"
              onClick={() => setActiveView(tab.id as any)}
              className="flex items-center gap-2"
            >
              {tab.label}
              {tab.count && (
                <Badge variant="secondary" className="text-xs px-1.5 py-0.5">
                  {tab.count}
                </Badge>
              )}
            </Button>
          ))}
        </div>
      </div>

      <div className="max-w-md mx-auto px-4 pb-6 space-y-6">
        {/* Active Challenge Preview - Always show */}
        {activeView === 'challenges' && (
          <AnimatedCard hoverScale>
            <Card className="bg-card-gradient border-0 overflow-hidden cursor-pointer" onClick={() => setShowChallenge(true)}>
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Badge className="bg-accent text-accent-foreground mb-2">
                      <Trophy size={12} className="mr-1" />
                      Active Challenge
                    </Badge>
                    <h2 className="text-lg font-semibold">{activeChallenge.title}</h2>
                    <p className="text-muted-foreground text-sm">{activeChallenge.subtitle}</p>
                  </div>
                  <ChevronRight size={20} className="text-muted-foreground" />
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="aspect-[16/9] rounded-lg overflow-hidden">
                  <img
                    src={activeChallenge.image}
                    alt={activeChallenge.title}
                    className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <span>{activeChallenge.deadline}</span>
                    <span>•</span>
                    <span>{activeChallenge.participants} participants</span>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold text-accent">+{activeChallenge.reward} pts</p>
                  </div>
                </div>

                <Button 
                  className="w-full bg-accent hover:bg-accent/90 text-accent-foreground"
                  onClick={(e) => {
                    e.stopPropagation()
                    setShowChallenge(true)
                  }}
                >
                  View Challenge Details
                </Button>
              </CardContent>
            </Card>
          </AnimatedCard>
        )}

        {/* Upcoming Events */}
        {activeView === 'events' && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Upcoming Events</h3>
            {upcomingEvents.map(event => (
              <AnimatedCard key={event.id} hoverScale>
                <Card className="bg-card-gradient border-0">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-start gap-3">
                        <div className="p-2 rounded-lg bg-primary/10">
                          <Calendar size={16} className="text-primary" />
                        </div>
                        <div>
                          <h4 className="font-medium text-foreground">{event.title}</h4>
                          <p className="text-sm text-muted-foreground">{event.date}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="secondary" className="text-xs">{event.type}</Badge>
                            <span className="text-xs text-muted-foreground flex items-center gap-1">
                              <Users size={10} />
                              {event.attendees} attending
                            </span>
                          </div>
                        </div>
                      </div>
                      <Button size="sm" variant="outline">
                        RSVP
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </AnimatedCard>
            ))}
          </div>
        )}

        {/* Social Feed */}
        {activeView === 'feed' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Community Feed</h3>
              <Button variant="ghost" size="sm" className="text-primary">
                <Camera size={16} className="mr-1" />
                Post
              </Button>
            </div>

            <SocialFeed />
          </div>
        )}
      </div>
    </div>
  )
}